import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import InputCst from "@components/InputCst/InputCst";
import SearchableMultiSelectListCst from "@components/SearchableMultiSelectListCst";
import { Stakeholder } from "@components/StakeholdersCst";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { ServicesURL } from "@utils/utilsApi";
import StakeholderListRow, {
  getDisplayName,
  getRowId,
} from "./components/StakeholderListRow";
import { CustomerListItem, StakeholdersPickerProps } from "./types";

const PAGE_SIZE = 10;
const ROLE_MAX_LENGTH = 50;

type Step = "select" | "configure";

const StakeholdersPickerCst: React.FC<StakeholdersPickerProps> = ({
  open,
  onClose,
  title,
  excludeUserIds,
  onConfirm,
}) => {
  const { t } = useTranslation("stakeholders-picker");
  const theme = useTheme();
  const { id: corporateId = "" } = useParams();

  const [step, setStep] = useState<Step>("select");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchSubmitted, setSearchSubmitted] = useState("");
  const [selectedUser, setSelectedUser] = useState<CustomerListItem | null>(
    null,
  );
  const [role, setRole] = useState("");
  const [visStakeholder, setVisStakeholder] = useState(false);
  const [roleError, setRoleError] = useState(false);

  const url = corporateId
    ? `${ServicesURL.getCustomer}?corporate_id=${corporateId}`
    : null;

  const { items, totalItems, totalPages, isLoading } =
    usePaginatedList<CustomerListItem>(url, {
      page,
      pageSize,
      search: searchSubmitted || undefined,
    });

  // Filtra gli utenti già associati come stakeholder.
  const visibleItems = useMemo(() => {
    if (!excludeUserIds || excludeUserIds.length === 0) return items;
    const exclude = new Set(excludeUserIds);
    return items.filter((it) => !exclude.has(getRowId(it)));
  }, [items, excludeUserIds]);

  useEffect(() => {
    if (!open) return;
    setStep("select");
    setPage(1);
    setSearchSubmitted("");
    setSelectedUser(null);
    setRole("");
    setVisStakeholder(false);
    setRoleError(false);
  }, [open]);

  const selectedSet = useMemo(
    () =>
      new Set<string | number>(selectedUser ? [getRowId(selectedUser)] : []),
    [selectedUser],
  );

  const handleToggle = (id: string | number, next: boolean) => {
    if (!next) {
      setSelectedUser(null);
      return;
    }
    const numericId = typeof id === "string" ? Number(id) : id;
    const found = visibleItems.find((it) => getRowId(it) === numericId);
    if (found) setSelectedUser(found);
  };

  const handleSearch = (value: string) => {
    setSearchSubmitted(value);
    setPage(1);
  };

  const handleNext = () => {
    if (!selectedUser) return;
    setStep("configure");
  };

  const handleBack = () => {
    setStep("select");
    setRoleError(false);
  };

  const handleAdd = () => {
    if (!selectedUser) return;
    const trimmed = role.trim();
    if (!trimmed) {
      setRoleError(true);
      return;
    }
    const stakeholder: Stakeholder = {
      userId: getRowId(selectedUser),
      name: getDisplayName(selectedUser),
      type: trimmed,
      visStakeholder,
    };
    onConfirm(stakeholder);
    onClose();
  };

  const canGoNext = !!selectedUser;
  const canAdd = !!selectedUser && role.trim().length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px 24px",
          color: theme.palette.text.primary,
        }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{ color: theme.palette.text.primary }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "12px 24px 16px" }}>
        {step === "select" ? (
          <SearchableMultiSelectListCst<CustomerListItem>
            selectionMode="single"
            showSelectedCount={false}
            items={visibleItems}
            getId={getRowId}
            renderRow={({ item }) => <StakeholderListRow item={item} />}
            selectedIds={selectedSet}
            onToggle={handleToggle}
            initialSearchValue={searchSubmitted}
            onSearchSubmit={handleSearch}
            searchPlaceholder={t("search-placeholder") || ""}
            searchButtonLabel={t("search-button") || ""}
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
            isLoading={isLoading}
            selectedCountLabel=""
            emptyLabel={t("empty") || ""}
          />
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {selectedUser && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  padding: "12px 16px",
                  borderRadius: "10px",
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.background.paper,
                }}
              >
                <StakeholderListRow item={selectedUser} />
              </Box>
            )}

            <InputCst
              id="stakeholder-role"
              label={`${t("role")} *`}
              value={role}
              onChange={(e: any) => {
                setRole(e.target.value);
                if (roleError && e.target.value.trim().length > 0) {
                  setRoleError(false);
                }
              }}
              maxValue={ROLE_MAX_LENGTH}
              error={roleError}
              errorMessage={roleError ? t("role-required") || "" : ""}
            />

            <FormControlLabel
              sx={{ color: theme.palette.text.primary }}
              control={
                <Switch
                  checked={visStakeholder}
                  onChange={(_, checked) => setVisStakeholder(checked)}
                />
              }
              label={t("show-to-learner")}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: "16px 24px 20px", gap: 2 }}>
        <Box sx={{ flex: 1 }} />
        {step === "select" ? (
          <Button
            onClick={handleNext}
            variant="contained"
            disabled={!canGoNext}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "8px",
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              "&:hover": { backgroundColor: theme.palette.primary.dark },
            }}
          >
            {t("next")}
          </Button>
        ) : (
          <>
            <Button
              onClick={handleBack}
              variant="outlined"
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "8px",
              }}
            >
              {t("back")}
            </Button>
            <Button
              onClick={handleAdd}
              variant="contained"
              disabled={!canAdd}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "8px",
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                "&:hover": { backgroundColor: theme.palette.primary.dark },
              }}
            >
              {t("add")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default StakeholdersPickerCst;
